import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export type MantineDateString = string | null;
export type MantineDateTimeString = string | null;

const DATE_FORMAT = "YYYY-MM-DD";
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const parseToDayjs = (value: unknown): dayjs.Dayjs | null => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  if (typeof value === "number") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const iso = dayjs(trimmed);
  if (iso.isValid()) return iso;

  const formats = [
    "DD MMM YYYY",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "DD-MM-YYYY",
    "DD-MM-YYYY HH:mm",
    "DD MMM YYYY - HH:mm",
    "DD MMM YYYY - HH:mm LT",
  ];

  for (const format of formats) {
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) return parsed;
  }

  return null;
};

/** Normalize app/Redux date values to Mantine v8 date string (YYYY-MM-DD). */
export const toMantineDateString = (value: unknown): MantineDateString => {
  const parsed = parseToDayjs(value);
  return parsed ? parsed.format(DATE_FORMAT) : null;
};

/** Normalize app values to Mantine v8 datetime string (YYYY-MM-DD HH:mm:ss). */
export const toMantineDateTimeString = (
  value: unknown,
): MantineDateTimeString => {
  const parsed = parseToDayjs(value);
  return parsed ? parsed.format(DATE_TIME_FORMAT) : null;
};

/** Convert Mantine v8 date/datetime string to ISO string for storage. */
export const fromMantineDateString = (
  value: MantineDateString | MantineDateTimeString,
): string | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
};

/** Convert Mantine v8 date string to Date (for maxDate/minDate when needed). */
export const mantineDateStringToDate = (
  value: MantineDateString,
): Date | null => {
  if (!value) return null;
  const parsed = dayjs(value, DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

/** Mantine v8 onChange handler adapter — keeps Date | null callback contract. */
export const mantineDateOnChange =
  (onChange: (date: Date | null) => void) => (value: string | null) => {
    if (!value) {
      onChange(null);
      return;
    }
    const parsed = dayjs(value);
    onChange(parsed.isValid() ? parsed.toDate() : null);
  };

/** Mantine v8 onChange handler adapter — keeps ISO string callback contract. */
export const mantineDateOnChangeToISO =
  (onChange: (value: string) => void) => (value: string | null) => {
    onChange(value ? (fromMantineDateString(value) ?? "") : "");
  };

/** Format Date bounds for Mantine v8 date components. */
export const toMantineDateBound = (
  date: Date | undefined,
): string | undefined => (date ? dayjs(date).format(DATE_FORMAT) : undefined);
